import React from 'react';
import { FileService } from '../services/fileService';

interface ProductImageProps {
  /** Görsel ID'si (null olabilir) */
  imageId: string | null;
  /** Alt metin */
  alt: string;
  /** Container boyutu - Tailwind CSS sınıfları */
  size?: string;
  /** SVG icon boyutu - Tailwind CSS sınıfları */
  iconSize?: string;
  /** Ek CSS sınıfları */
  className?: string;
  /** Container'a ek CSS sınıfları */
  containerClassName?: string;
  /** Tıklama olayı */
  onClick?: () => void;
}

/**
 * Ürün görseli component'i
 * 
 * Bu component tüm ürün görsellerini standart bir şekilde gösterir:
 * - Görsel varsa FileService.getThumbnailUrl ile yükler
 * - Görsel yoksa inline SVG placeholder gösterir
 * - Yükleme hatası durumunda SVG placeholder'a geçer
 * - Sonsuz döngü problemi yok
 * - Dark mode desteği
 * - Responsive boyutlandırma
 * 
 * @example
 * // Küçük ürün görseli (16x16)
 * <ProductImage imageId={item.Gorsel} alt={item.Ad} size="w-16 h-16" iconSize="w-8 h-8" />
 * 
 * // Büyük modal görseli (24x24)
 * <ProductImage imageId={product.Gorsel} alt={product.Ad} size="w-24 h-24" iconSize="w-12 h-12" />
 * 
 * // Özel stil ile
 * <ProductImage 
 *   imageId={part.Gorsel} 
 *   alt={part.Ad} 
 *   size="w-20 h-20" 
 *   iconSize="w-10 h-10"
 *   containerClassName="border-2 border-blue-500"
 *   onClick={() => openModal()}
 * />
 */
const ProductImage: React.FC<ProductImageProps> = ({
  imageId,
  alt,
  size = "w-16 h-16",
  iconSize = "w-8 h-8",
  className = "",
  containerClassName = "",
  onClick
}) => {
  // Görsel URL'sini oluştur
  const getImageUrl = (id: string | null) => {
    if (!id) return null;
    return FileService.getThumbnailUrl(id);
  };

  // SVG Placeholder Icon
  const PlaceholderIcon = () => (
    <svg 
      className={`${iconSize} text-gray-400 dark:text-gray-500`} 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
      />
    </svg>
  );

  // Error handler for broken images
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
    
    const parent = target.parentElement;
    if (parent && !parent.querySelector('.placeholder-svg')) {
      const placeholderDiv = document.createElement('div');
      placeholderDiv.className = 'placeholder-svg w-full h-full flex items-center justify-center';
      placeholderDiv.innerHTML = `
        <svg class="${iconSize} text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
      `;
      parent.appendChild(placeholderDiv);
    }
  };

  return (
    <div 
      className={`${size} bg-white dark:bg-gray-700 rounded-lg overflow-hidden p-1 border border-gray-200 dark:border-gray-600 ${containerClassName} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {imageId ? (
        <img
          src={getImageUrl(imageId)!}
          alt={alt}
          className={`w-full h-full object-contain ${className}`}
          onError={handleImageError}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <PlaceholderIcon />
        </div>
      )}
    </div>
  );
};

export default ProductImage;
